/**
 * docsData.js — Static reference data for the Documentation / Query Builder screen.
 *
 * Covers: query types, sports/leagues, sportsbook tiers, example queries.
 * Dynamic data (markets, players) is loaded from Cloud Functions at runtime.
 */

// ─── Query Types ─────────────────────────────────────────────────────────────

export const QUERY_TYPES = [
  {
    id: 'STRAIGHT',
    label: 'Straight Bet',
    icon: 'trending-up-outline',
    color: '#60a5fa',
    description:
      'Look up a specific team, matchup, or market. Returns moneylines, spreads, totals, and more for all available sportsbooks.',
    parameters: ['sport', 'league', 'team', 'market', 'line', 'direction'],
    supports: ['Moneyline', 'Spread', 'Total (O/U)', 'Team Total', 'Player Props'],
    examples: [
      { label: 'Team moneyline', query: 'lakers moneyline' },
      { label: 'Spread with line', query: 'chiefs -3.5' },
      { label: 'Over/under total', query: 'celtics vs bucks over 220' },
      { label: 'Team total', query: 'yankees team total over 4.5' },
    ],
  },
  {
    id: 'SGP',
    label: 'Same Game Parlay',
    icon: 'git-merge-outline',
    color: '#a78bfa',
    description:
      'Build a same-game parlay by combining multiple legs from the same game. Mix team lines with player props. Returns the best available odds across all books.',
    parameters: ['sport', 'league', 'team', 'player', 'market', 'line', 'direction'],
    supports: ['Multi-leg SGP', 'Mixed team + player legs', 'Any sport that supports SGP'],
    examples: [
      { label: 'Team + player prop', query: 'lakers sgp lebron 25+ points and lakers -3.5' },
      { label: 'Multi-player parlay', query: 'mahomes 300+ yards and kelce 75+ receiving yards sgp' },
      { label: 'Game total + player', query: 'celtics game sgp jayson tatum 30+ and over 220' },
    ],
  },
  {
    id: 'LIVE',
    label: 'Live / In-Game',
    icon: 'radio-outline',
    color: '#f87171',
    description:
      'Add the word "live" to any query to search in-game lines instead of pre-game odds. Works with all other query types.',
    parameters: ['any regular query parameters', '"live" keyword'],
    supports: ['Live moneylines', 'Live spreads', 'Live totals', 'Live player props'],
    examples: [
      { label: 'Live game lines', query: 'live lakers game' },
      { label: 'Live team spread', query: 'live chiefs spread' },
      { label: 'Live player prop', query: 'live lebron points' },
    ],
  },
  {
    id: 'BEST_BETS_ALL',
    label: 'Best Bets',
    icon: 'star-outline',
    color: '#fbbf24',
    description:
      'Find the best value bets across all sportsbooks for a sport or league. Ranked by Expected Value (EV). Optionally pin to a specific sportsbook.',
    parameters: ['sport', 'league', 'sportsbook (optional)'],
    supports: ['EV-ranked results', 'All major sportsbooks', 'Any sport/league'],
    examples: [
      { label: 'Best NBA bets', query: 'best nba bets today' },
      { label: 'Best NFL picks', query: 'best nfl picks tonight' },
      { label: 'Best soccer bets', query: 'best premier league bets' },
      { label: 'Best bets on one book', query: 'best bets on draftkings' },
    ],
  },
  {
    id: 'BEST_BETS_SPORTSBOOK',
    label: 'Book-Specific Best Bets',
    icon: 'bookmark-outline',
    color: '#34d399',
    description:
      'Find the best bets available on a specific sportsbook. Pair a book name with a sport or "best bets" to narrow results.',
    parameters: ['sportsbook', 'sport (optional)', 'league (optional)'],
    supports: ['All Tier 1–3 sportsbooks', 'Any sport/league'],
    examples: [
      { label: 'DraftKings NBA', query: 'draftkings nba bets' },
      { label: 'FanDuel best picks', query: 'fanduel best bets tonight' },
      { label: 'Caesars NFL lines', query: 'caesars nfl best bets' },
    ],
  },
  {
    id: 'MATCHUP_QUERY',
    label: 'Matchup',
    icon: 'swap-horizontal-outline',
    color: '#fb923c',
    description:
      'Look up a specific game matchup. Returns all available markets for that fixture, including moneylines, spreads, totals, and player props.',
    parameters: ['team1', '"vs" or "@"', 'team2'],
    supports: ['All game markets', 'Player props for the game', 'Live lines'],
    examples: [
      { label: 'Team vs team', query: 'celtics vs bucks' },
      { label: 'Away @ home', query: 'warriors @ nuggets' },
      { label: 'With market', query: 'eagles vs cowboys spread' },
    ],
  },
  {
    id: 'SPECIFIC_PLAYER_MARKET',
    label: 'Player + Market + Line',
    icon: 'person-outline',
    color: '#e879f9',
    description:
      'Find odds for a specific player prop with a direction and line. Use "+" or "over/under" to specify the threshold.',
    parameters: ['player name', 'line (number)', 'direction (over/under)', 'market'],
    supports: ['Any player prop market', 'Over/Under with specific lines', 'All player prop sportsbooks (DFS + traditional)'],
    examples: [
      { label: 'Points over', query: 'lebron 25+ points' },
      { label: 'Exact line with direction', query: 'mahomes over 300 passing yards' },
      { label: 'Assists threshold', query: 'doncic 8+ assists' },
      { label: 'Under with line', query: 'judge under 1.5 home runs' },
    ],
  },
  {
    id: 'PLAYER_PROP',
    label: 'Player Props (Browse)',
    icon: 'people-outline',
    color: '#38bdf8',
    description:
      'Browse all available props for a player across all sportsbooks. No line required — returns all available markets and lines.',
    parameters: ['player name'],
    supports: ['All available prop markets', 'DFS books included', 'Multi-sport'],
    examples: [
      { label: 'All props for a player', query: 'lebron james props' },
      { label: 'Pitcher props', query: 'shohei ohtani props' },
      { label: 'Receiver props', query: 'tyreek hill props today' },
    ],
  },
  {
    id: 'TEAM_MARKETS',
    label: 'Team Markets',
    icon: 'shield-outline',
    color: '#4ade80',
    description:
      'Type a team name alone to browse all available betting markets for that team. Returns moneylines, spreads, totals, and team-specific props.',
    parameters: ['team name'],
    supports: ['All game markets', 'Team-level props'],
    examples: [
      { label: 'Team name only', query: 'cowboys' },
      { label: 'Full team name', query: 'golden state warriors' },
      { label: 'City name', query: 'dallas' },
    ],
  },
  {
    id: 'POSITION_BASED_QUERY',
    label: 'Position-Based',
    icon: 'body-outline',
    color: '#f9a8d4',
    description:
      'Query by team + position to find relevant player props. Great for targeting a specific role on a team.',
    parameters: ['team name', 'position abbreviation', 'market (optional)'],
    supports: ['NFL: QB, RB, WR, TE, K', 'NBA: PG, SG, SF, PF, C', 'MLB: SP, RP, C, 1B, 2B, 3B, SS, OF'],
    examples: [
      { label: 'NFL QB stats', query: 'rams qb passing yards' },
      { label: 'NBA point guard', query: 'lakers pg assists' },
      { label: 'Position only', query: 'eagles wr receiving yards' },
    ],
  },
];

// ─── Sports + Leagues ─────────────────────────────────────────────────────────

export const SPORTS_LEAGUES = [
  {
    sport: 'Basketball',
    sportId: 'basketball',
    icon: 'basketball-outline',
    color: '#f97316',
    leagues: [
      { id: 'nba', label: 'NBA', type: 'team', priority: 1, example: 'lakers' },
      { id: 'ncaab', label: 'NCAAB', type: 'team', priority: 1, example: 'duke basketball' },
      { id: 'wnba', label: 'WNBA', type: 'team', priority: 2, example: 'wnba best bets' },
      { id: 'nba_g_league', label: 'G League', type: 'team', priority: 3, example: 'g league bets' },
      { id: 'euroleague', label: 'EuroLeague', type: 'team', priority: 2, example: 'euroleague bets' },
    ],
  },
  {
    sport: 'Football',
    sportId: 'football',
    icon: 'american-football-outline',
    color: '#84cc16',
    leagues: [
      { id: 'nfl', label: 'NFL', type: 'team', priority: 1, example: 'chiefs spread' },
      { id: 'ncaaf', label: 'NCAAF', type: 'team', priority: 1, example: 'alabama vs georgia' },
      { id: 'cfl', label: 'CFL', type: 'team', priority: 2, example: 'cfl bets' },
      { id: 'xfl', label: 'XFL', type: 'team', priority: 3, example: 'xfl best bets' },
      { id: 'ufl', label: 'UFL', type: 'team', priority: 3, example: 'ufl bets' },
    ],
  },
  {
    sport: 'Baseball',
    sportId: 'baseball',
    icon: 'baseball-outline',
    color: '#22c55e',
    leagues: [
      { id: 'mlb', label: 'MLB', type: 'team', priority: 1, example: 'yankees moneyline' },
      { id: 'ncaa_baseball', label: 'NCAA Baseball', type: 'team', priority: 2, example: 'ncaa baseball bets' },
      { id: 'kbo', label: 'KBO', type: 'team', priority: 2, example: 'kbo best bets' },
      { id: 'npb', label: 'NPB', type: 'team', priority: 2, example: 'npb bets' },
    ],
  },
  {
    sport: 'Hockey',
    sportId: 'hockey',
    icon: 'snow-outline',
    color: '#38bdf8',
    leagues: [
      { id: 'nhl', label: 'NHL', type: 'team', priority: 1, example: 'bruins moneyline' },
      { id: 'ahl', label: 'AHL', type: 'team', priority: 2, example: 'ahl bets' },
      { id: 'khl', label: 'KHL', type: 'team', priority: 2, example: 'khl bets' },
      { id: 'pwhl', label: 'PWHL', type: 'team', priority: 2, example: 'pwhl bets' },
    ],
  },
  {
    sport: 'Soccer',
    sportId: 'soccer',
    icon: 'football-outline',
    color: '#4ade80',
    leagues: [
      { id: 'england_-_premier_league', label: 'Premier League', type: 'team', priority: 1, example: 'arsenal vs liverpool' },
      { id: 'spain_-_la_liga', label: 'La Liga', type: 'team', priority: 1, example: 'real madrid moneyline' },
      { id: 'germany_-_bundesliga', label: 'Bundesliga', type: 'team', priority: 1, example: 'bundesliga best bets' },
      { id: 'italy_-_serie_a', label: 'Serie A', type: 'team', priority: 1, example: 'serie a bets' },
      { id: 'france_-_ligue_1', label: 'Ligue 1', type: 'team', priority: 1, example: 'psg moneyline' },
      { id: 'uefa_-_champions_league', label: 'Champions League', type: 'team', priority: 1, example: 'ucl best bets' },
      { id: 'usa_-_major_league_soccer', label: 'MLS', type: 'team', priority: 1, example: 'mls best bets tonight' },
      { id: 'mexico_-_liga_mx', label: 'Liga MX', type: 'team', priority: 1, example: 'liga mx bets' },
    ],
  },
  {
    sport: 'MMA / Combat',
    sportId: 'mma',
    icon: 'fitness-outline',
    color: '#f87171',
    leagues: [
      { id: 'ufc', label: 'UFC', type: 'individual', priority: 1, example: 'ufc best bets' },
      { id: 'bellator', label: 'Bellator', type: 'individual', priority: 2, example: 'bellator bets' },
      { id: 'boxing', label: 'Boxing', type: 'individual', priority: 2, example: 'boxing best bets' },
    ],
  },
  {
    sport: 'Tennis',
    sportId: 'tennis',
    icon: 'tennisball-outline',
    color: '#a3e635',
    leagues: [
      { id: 'atp', label: "ATP (Men's)", type: 'individual', priority: 1, example: 'atp best bets' },
      { id: 'wta', label: "WTA (Women's)", type: 'individual', priority: 1, example: 'wta bets' },
      { id: 'atp_challenger', label: 'ATP Challenger', type: 'individual', priority: 2, example: 'challenger tennis bets' },
    ],
  },
  {
    sport: 'Golf',
    sportId: 'golf',
    icon: 'golf-outline',
    color: '#86efac',
    leagues: [
      { id: 'pga', label: 'PGA Tour', type: 'individual', priority: 1, example: 'pga best bets this week' },
      { id: 'lpga', label: 'LPGA', type: 'individual', priority: 2, example: 'lpga bets' },
      { id: 'liv', label: 'LIV Golf', type: 'individual', priority: 2, example: 'liv golf bets' },
    ],
  },
  {
    sport: 'Racing',
    sportId: 'racing',
    icon: 'speedometer-outline',
    color: '#fbbf24',
    leagues: [
      { id: 'formula_1', label: 'Formula 1', type: 'individual', priority: 1, example: 'f1 best bets' },
      { id: 'nascar_cup', label: 'NASCAR Cup', type: 'individual', priority: 1, example: 'nascar bets this week' },
      { id: 'indycar', label: 'IndyCar', type: 'individual', priority: 2, example: 'indycar bets' },
    ],
  },
  {
    sport: 'Esports',
    sportId: 'esports',
    icon: 'game-controller-outline',
    color: '#c084fc',
    leagues: [
      { id: 'cs2', label: 'CS2', type: 'team', priority: 1, example: 'cs2 best bets' },
      { id: 'league_of_legends', label: 'League of Legends', type: 'team', priority: 1, example: 'lol bets' },
      { id: 'valorant', label: 'Valorant', type: 'team', priority: 2, example: 'valorant bets' },
      { id: 'dota2', label: 'Dota 2', type: 'team', priority: 2, example: 'dota 2 bets' },
    ],
  },
];

// ─── Sportsbook Tiers ─────────────────────────────────────────────────────────
// All 339 books from the OpticOdds API, organized into 6 operational tiers.

export const SPORTSBOOK_TIERS = [
  {
    tier: 1,
    label: 'Tier 1 — Major US Books',
    description: 'The largest US operators with deepest markets and widest state availability. Checked first on every query. These have the most reliable real-time odds.',
    color: '#fbbf24',
    books: [
      { id: 'draftkings', label: 'DraftKings' },
      { id: 'fanduel', label: 'FanDuel' },
      { id: 'betmgm', label: 'BetMGM' },
      { id: 'caesars', label: 'Caesars' },
      { id: 'betrivers', label: 'BetRivers' },
      { id: 'espn_bet', label: 'ESPN Bet' },
      { id: 'hard_rock', label: 'Hard Rock Bet' },
      { id: 'unibet', label: 'Unibet' },
      { id: 'fanatics', label: 'Fanatics' },
      { id: 'bally_bet', label: 'Bally Bet' },
      { id: 'borgata', label: 'Borgata' },
      { id: 'betparx', label: 'BetParx' },
      { id: 'superbook', label: 'SuperBook' },
      { id: 'tipico', label: 'Tipico' },
      { id: 'si', label: 'SI Sportsbook' },
      { id: 'betway', label: 'Betway' },
      { id: 'wynnbet', label: 'WynnBET' },
      { id: 'pointsbet', label: 'PointsBet' },
      { id: 'circa_sports', label: 'Circa Sports' },
      { id: 'circa_vegas', label: 'Circa Vegas' },
      { id: 'thescore', label: 'theScore Bet' },
      { id: 'golden_nugget', label: 'Golden Nugget' },
      { id: 'betfred', label: 'Betfred' },
      { id: 'betonline', label: 'BetOnline' },
      { id: 'bovada', label: 'Bovada' },
      { id: 'mybookie', label: 'MyBookie' },
      { id: 'betus', label: 'BetUS' },
      { id: 'fliff', label: 'Fliff' },
      { id: 'novig', label: 'Novig' },
      { id: 'betr', label: 'Betr' },
      { id: 'barstool', label: 'Barstool' },
      { id: 'fox_bet', label: 'Fox Bet' },
      { id: 'desert_diamond', label: 'Desert Diamond' },
      { id: 'four_winds', label: 'Four Winds' },
      { id: 'play_eagle', label: 'Play Eagle' },
    ],
  },
  {
    tier: 2,
    label: 'Tier 2 — Exchanges & Sharp Lines',
    description: 'Betting exchanges and sharp-focused books with low or no vig. Best for finding true market prices and calibrating EV calculations.',
    color: '#f87171',
    books: [
      { id: 'pinnacle', label: 'Pinnacle' },
      { id: 'betfair_exchange', label: 'Betfair Exchange' },
      { id: 'betfair_exchange_lay_', label: 'Betfair Exchange (Lay)' },
      { id: 'betfair', label: 'Betfair' },
      { id: 'prophet_x', label: 'Prophet X' },
      { id: 'sporttrade', label: 'Sporttrade' },
      { id: 'kalshi', label: 'Kalshi' },
      { id: 'polymarket', label: 'Polymarket' },
      { id: 'polymarket_usa_', label: 'Polymarket USA' },
      { id: 'robinhood', label: 'Robinhood' },
      { id: 'stake', label: 'Stake' },
      { id: 'smarkets', label: 'Smarkets' },
      { id: 'prophet_exchange', label: 'Prophet Exchange' },
      { id: 'pinny', label: 'Pinny' },
      { id: 'sx_bet', label: 'SX Bet' },
      { id: 'sharp', label: 'Sharp' },
      { id: 'betfair_exchange_australia_', label: 'Betfair Exchange AU' },
      { id: 'betfair_exchange_australia_lay_', label: 'Betfair Exchange AU Lay' },
    ],
  },
  {
    tier: 3,
    label: 'Tier 3 — International',
    description: 'Global sportsbooks covering major international leagues. Many offer unique markets or better lines for non-US sports.',
    color: '#a78bfa',
    books: [
      { id: '888sport', label: '888Sport' },
      { id: '888sport_canada_', label: '888Sport Canada' },
      { id: '1xbet', label: '1xBet' },
      { id: '10bet', label: '10Bet' },
      { id: '22bet', label: '22Bet' },
      { id: 'bet105', label: 'Bet105' },
      { id: 'bet99', label: 'Bet99' },
      { id: 'betano', label: 'Betano' },
      { id: 'betano_argentina_', label: 'Betano Argentina' },
      { id: 'betano_chile_', label: 'Betano Chile' },
      { id: 'betcris', label: 'BetCris' },
      { id: 'betsson', label: 'Betsson' },
      { id: 'betsson_chile_', label: 'Betsson Chile' },
      { id: 'betvictor', label: 'BetVictor' },
      { id: 'betuk', label: 'BetUK' },
      { id: 'bwin', label: 'bwin' },
      { id: 'casumo', label: 'Casumo' },
      { id: 'comeon_', label: 'ComeOn' },
      { id: 'coolbet', label: 'Coolbet' },
      { id: 'coral', label: 'Coral' },
      { id: 'dafabet', label: 'Dafabet' },
      { id: 'dazn_bet', label: 'DAZN Bet' },
      { id: 'ggbet', label: 'GGBet' },
      { id: 'guts', label: 'Guts' },
      { id: 'ladbrokes', label: 'Ladbrokes' },
      { id: 'leovegas', label: 'LeoVegas' },
      { id: 'lottoland', label: 'LottoLand' },
      { id: 'mr_green', label: 'Mr. Green' },
      { id: 'paddy_power', label: 'Paddy Power' },
      { id: 'parimatch_brazil_', label: 'Parimatch Brazil' },
      { id: 'parimatch_india_', label: 'Parimatch India' },
      { id: 'partypoker', label: 'PartyPoker' },
      { id: 'rizk', label: 'Rizk' },
      { id: 'royal_panda', label: 'Royal Panda' },
      { id: 'saba_sports', label: 'SABA Sports' },
      { id: 'sbobet', label: 'SBOBET' },
      { id: 'sbk', label: 'SBK' },
      { id: 'sky_bet', label: 'Sky Bet' },
      { id: 'superbet', label: 'Superbet' },
      { id: 'tipsport', label: 'Tipsport' },
      { id: 'tonybet', label: 'TonyBet' },
      { id: 'unibet_denmark_', label: 'Unibet Denmark' },
      { id: 'unibet_sweden_', label: 'Unibet Sweden' },
      { id: 'unibet_united_kingdom_', label: 'Unibet UK' },
      { id: 'veikkaus', label: 'Veikkaus' },
      { id: 'william_hill', label: 'William Hill' },
      { id: 'william_hill_europe_', label: 'William Hill Europe' },
      { id: 'william_hill_united_kingdom_', label: 'William Hill UK' },
      { id: 'boyle_sports', label: 'Boyle Sports' },
      { id: 'betamapola', label: 'BetAmapola' },
      { id: 'campeonbet', label: 'Campeonbet' },
      { id: 'codere', label: 'Codere' },
      { id: 'galera_bet', label: 'Galera Bet' },
      { id: 'jugabet_argentina_', label: 'Jugabet Argentina' },
      { id: 'jugabet_chile_', label: 'Jugabet Chile' },
      { id: 'midnite', label: 'Midnite' },
      { id: 'ninja_casino', label: 'Ninja Casino' },
      { id: 'thunderpick', label: 'Thunderpick' },
      { id: 'sportingbet', label: 'Sportingbet' },
      { id: 'sportingbet_united_kingdom_', label: 'Sportingbet UK' },
      { id: 'bc_game', label: 'BC.Game' },
      { id: 'betdex', label: 'BetDex' },
      { id: 'betsafe', label: 'BetSafe' },
      { id: 'rivalry', label: 'Rivalry' },
      { id: 'blue_book', label: 'Blue Book' },
      { id: 'bookmaker', label: 'Bookmaker' },
      { id: 'crypto_com', label: 'Crypto.com' },
    ],
  },
  {
    tier: 4,
    label: 'Tier 4 — Regional US Variants',
    description: 'State and province-specific versions of major US sportsbooks. Same operator as the parent book but operating under a separate state license.',
    color: '#60a5fa',
    books: [
      { id: 'betrivers_new_york_', label: 'BetRivers NY' },
      { id: 'betrivers_ontario_', label: 'BetRivers Ontario' },
      { id: 'caesars_colorado_', label: 'Caesars CO' },
      { id: 'caesars_illinois_', label: 'Caesars IL' },
      { id: 'caesars_louisiana_', label: 'Caesars LA' },
      { id: 'caesars_maine_', label: 'Caesars ME' },
      { id: 'caesars_michigan_', label: 'Caesars MI' },
      { id: 'caesars_nevada_', label: 'Caesars NV' },
      { id: 'caesars_new_york_', label: 'Caesars NY' },
      { id: 'caesars_north_carolina_', label: 'Caesars NC' },
      { id: 'caesars_ontario_', label: 'Caesars Ontario' },
      { id: 'caesars_pennsylvania_', label: 'Caesars PA' },
      { id: 'caesars_tennessee_', label: 'Caesars TN' },
      { id: 'draftkings_illinois_', label: 'DraftKings IL' },
      { id: 'draftkings_tennessee_', label: 'DraftKings TN' },
      { id: 'hard_rock_arizona_', label: 'Hard Rock AZ' },
      { id: 'hard_rock_iowa_', label: 'Hard Rock IA' },
      { id: 'hard_rock_new_jersey_', label: 'Hard Rock NJ' },
      { id: 'hard_rock_virginia_', label: 'Hard Rock VA' },
      { id: 'fox_bet_michigan_', label: 'Fox Bet MI' },
      { id: 'fox_bet_pennsylvania_', label: 'Fox Bet PA' },
      { id: 'golden_nugget_michigan_', label: 'Golden Nugget MI' },
      { id: 'golden_nugget_new_jersey_', label: 'Golden Nugget NJ' },
      { id: 'pointsbet_colorado_', label: 'PointsBet CO' },
      { id: 'pointsbet_illinois_', label: 'PointsBet IL' },
      { id: 'pointsbet_indiana_', label: 'PointsBet IN' },
      { id: 'pointsbet_iowa_', label: 'PointsBet IA' },
      { id: 'pointsbet_kansas_', label: 'PointsBet KS' },
      { id: 'pointsbet_louisiana_', label: 'PointsBet LA' },
      { id: 'pointsbet_maryland_', label: 'PointsBet MD' },
      { id: 'pointsbet_michigan_', label: 'PointsBet MI' },
      { id: 'pointsbet_new_jersey_', label: 'PointsBet NJ' },
      { id: 'pointsbet_new_york_', label: 'PointsBet NY' },
      { id: 'pointsbet_ohio_', label: 'PointsBet OH' },
      { id: 'pointsbet_ontario_', label: 'PointsBet Ontario' },
      { id: 'pointsbet_pennsylvania_', label: 'PointsBet PA' },
      { id: 'pointsbet_virginia_', label: 'PointsBet VA' },
      { id: 'pointsbet_west_virginia_', label: 'PointsBet WV' },
      { id: 'sporttrade_arizona_', label: 'Sporttrade AZ' },
      { id: 'sporttrade_colorado_', label: 'Sporttrade CO' },
      { id: 'sporttrade_iowa_', label: 'Sporttrade IA' },
      { id: 'sporttrade_virginia_', label: 'Sporttrade VA' },
      { id: 'wynnbet_massachusetts_', label: 'WynnBET MA' },
      { id: 'wynnbet_michigan_', label: 'WynnBET MI' },
      { id: 'wynnbet_new_jersey_', label: 'WynnBET NJ' },
      { id: 'wynnbet_new_york_', label: 'WynnBET NY' },
      { id: 'wynnbet_west_virginia_', label: 'WynnBET WV' },
      { id: 'betly', label: 'BetLy' },
      { id: 'betly_ohio_', label: 'BetLy OH' },
      { id: 'betly_tennessee_', label: 'BetLy TN' },
      { id: 'betway_alaska_', label: 'Betway AK' },
      { id: 'betway_arizona_', label: 'Betway AZ' },
      { id: 'betway_canada_', label: 'Betway Canada' },
      { id: 'betway_colorado_', label: 'Betway CO' },
      { id: 'betway_iowa_', label: 'Betway IA' },
      { id: 'betway_new_jersey_', label: 'Betway NJ' },
      { id: 'betway_ohio_', label: 'Betway OH' },
      { id: 'betway_virginia_', label: 'Betway VA' },
      { id: 'sugarhouse', label: 'SugarHouse' },
      { id: 'wind_creek', label: 'Wind Creek' },
      { id: 'firekeepers', label: 'FireKeepers' },
      { id: 'gambetdc', label: 'GambetDC' },
      { id: 'crab_sports', label: 'Crab Sports' },
      { id: 'play_maverick', label: 'Play Maverick' },
      { id: 'stn_sports', label: 'STN Sports' },
      { id: 'resorts', label: 'Resorts Casino' },
      { id: 'resorts_world_bet', label: 'Resorts World Bet' },
      { id: 'westgate', label: 'Westgate' },
      { id: 'elite_bet', label: 'Elite Bet' },
      { id: 'elite_sportsbook', label: 'Elite Sportsbook' },
      { id: 'twinspires', label: 'TwinSpires' },
      { id: 'fanatics_markets', label: 'Fanatics Markets' },
      { id: 'betm', label: 'BetM' },
      { id: 'prime_sports', label: 'Prime Sports' },
      { id: 'maximbet', label: 'MaximBet' },
      { id: 'betwhale', label: 'BetWhale' },
      { id: 'betwildwood', label: 'BetWildwood' },
      { id: 'action_24_7', label: 'Action 24/7' },
    ],
  },
  {
    tier: 5,
    label: 'Tier 5 — DFS & Pick-Em Platforms',
    description: 'Fantasy sports and pick-em apps. Most are player props only — no moneylines, spreads, or game totals. Some have unique multiplier formats.',
    color: '#34d399',
    books: [
      { id: 'prizepicks', label: 'PrizePicks' },
      { id: 'prizepicks_5_or_6_pick_flex_', label: 'PrizePicks Flex' },
      { id: 'prizepicks_demons_and_goblins_', label: 'PrizePicks D&G' },
      { id: 'underdog_fantasy', label: 'Underdog Fantasy' },
      { id: 'underdog_fantasy_3_or_5_pick_', label: 'Underdog 3-5 Pick' },
      { id: 'underdog_fantasy_6_pick_', label: 'Underdog 6 Pick' },
      { id: 'underdog_fantasy_multipliers_', label: 'Underdog Multipliers' },
      { id: 'underdog_predictions', label: 'Underdog Predictions' },
      { id: 'underdog_sportsbook', label: 'Underdog Sportsbook' },
      { id: 'sleeper', label: 'Sleeper' },
      { id: 'hotstreak', label: 'HotStreak' },
      { id: 'jock_mkt', label: 'Jock MKT' },
      { id: 'jock_mkt_4_pick_', label: 'Jock MKT 4-Pick' },
      { id: 'jock_mkt_5_pick_', label: 'Jock MKT 5-Pick' },
      { id: 'mojo_fantasy', label: 'Mojo Fantasy' },
      { id: 'vivid_picks', label: 'Vivid Picks' },
      { id: 'vivid_picks_4_pick_', label: 'Vivid Picks 4-Pick' },
      { id: 'parlayplay', label: 'ParlayPlay' },
      { id: 'parlayplay_4_pick_', label: 'ParlayPlay 4-Pick' },
      { id: 'parlayplay_6_pick_', label: 'ParlayPlay 6-Pick' },
      { id: 'thrivefantasy', label: 'ThriveFantasy' },
      { id: 'chalkboard', label: 'Chalkboard' },
      { id: 'boom_fantasy', label: 'Boom Fantasy' },
      { id: 'boom_fantasy_5_pick_insured_', label: 'Boom 5-Pick Insured' },
      { id: 'betr_picks', label: 'Betr Picks' },
      { id: 'betr_picks_all_', label: 'Betr Picks All' },
      { id: 'betr_picks_boosted_', label: 'Betr Picks Boosted' },
      { id: 'draftkings_pick_2_', label: 'DK Pick2' },
      { id: 'draftkings_pick_3_', label: 'DK Pick3' },
      { id: 'draftkings_pick_6_', label: 'DK Pick6' },
      { id: 'draftkings_pick_6_multipliers_', label: 'DK Pick6 Multipliers' },
      { id: 'draftkings_predictions', label: 'DK Predictions' },
      { id: 'superdraft_3_or_5_pick_', label: 'SuperDraft' },
      { id: 'dabble_3_or_5_pick_', label: 'Dabble 3-5 Pick' },
      { id: 'dabble_3_or_5_pick_multipliers_', label: 'Dabble Multipliers' },
      { id: 'dabble_shields_', label: 'Dabble Shields' },
      { id: 'betopenly', label: 'BetOpenly' },
      { id: 'betopenly_instant_dfs_', label: 'BetOpenly DFS' },
      { id: 'epick', label: 'ePick' },
      { id: 'epick_50_50', label: 'ePick 50/50' },
      { id: 'ownersbox', label: 'OwnersBox' },
      { id: 'ownersbox_6_pick_insured_', label: 'OwnersBox 6-Pick' },
      { id: 'props_builder', label: 'Props Builder' },
      { id: 'sportsbattle', label: 'SportsBattle' },
      { id: 'sportsbattle_5_pick_power_', label: 'SportsBattle 5-Pick' },
      { id: 'sportsquack_5_pick_', label: 'Sportsquack 5-Pick' },
      { id: 'monkey_knife_fight', label: 'Monkey Knife Fight' },
      { id: 'overtime', label: 'Overtime' },
      { id: 'wanna_parlay', label: 'Wanna Parlay' },
      { id: 'no_house_advantage', label: 'No House Advantage' },
      { id: 'no_house_advantage_4_pick_', label: 'NHA 4-Pick' },
      { id: 'no_house_advantage_5_pick_insurance_', label: 'NHA 5-Pick Insurance' },
      { id: 'no_house_advantage_5_pick_power_', label: 'NHA 5-Pick Power' },
      { id: 'rebet', label: 'Rebet' },
      { id: 'rebet_props_city', label: 'Rebet Props City' },
      { id: 'parlaye', label: 'Parlaye' },
      { id: 'thrillzz', label: 'Thrillzz' },
    ],
  },
  {
    tier: 6,
    label: 'Tier 6 — Australia, Canada & Extended',
    description: 'Australian and Canadian books, plus offshore and specialty operators. Many focus on specific regions or sports.',
    color: '#e879f9',
    books: [
      { id: 'tab', label: 'TAB' },
      { id: 'tab_new_zealand_', label: 'TAB NZ' },
      { id: 'tabtouch', label: 'TABtouch' },
      { id: 'neds', label: 'Neds' },
      { id: 'sportsbet', label: 'Sportsbet AU' },
      { id: 'ladbrokes_australia_', label: 'Ladbrokes AU' },
      { id: 'pointsbet_australia_', label: 'PointsBet AU' },
      { id: 'play_alberta', label: 'Play Alberta' },
      { id: 'playnow', label: 'PlayNow' },
      { id: 'proline', label: 'Proline' },
      { id: 'sports_interaction', label: 'Sports Interaction' },
      { id: 'topsport', label: 'TopSport' },
      { id: 'betdeluxe', label: 'BetDeluxe' },
      { id: 'bluebet', label: 'BlueBet' },
      { id: 'boombet', label: 'BoomBet' },
      { id: 'bookmaker_australia_', label: 'Bookmaker AU' },
      { id: 'betstar', label: 'BetStar' },
      { id: 'betr_australia_', label: 'Betr AU' },
      { id: 'palmerbet', label: 'PalmerBet' },
      { id: 'playup', label: 'PlayUp' },
      { id: 'playup_australia_', label: 'PlayUp AU' },
      { id: 'unibet_australia_', label: 'Unibet AU' },
      { id: 'picklebet', label: 'Picklebet' },
      { id: 'bet_right', label: 'Bet Right' },
      { id: 'clutchbet', label: 'ClutchBet' },
      { id: 'spinbet', label: 'SpinBet' },
      { id: 'atg', label: 'ATG' },
      { id: 'northstar_bets', label: 'NorthStar Bets' },
      { id: 'powerplay', label: 'PowerPlay' },
      { id: 'spin_sports', label: 'Spin Sports' },
      { id: 'mise-o-jeu', label: 'Mise-O-Jeu' },
      { id: 'dabble_australia_', label: 'Dabble AU' },
      { id: '5dimes', label: '5Dimes' },
      { id: 'action_24_7', label: 'Action 24/7' },
      { id: '4cx', label: '4CX' },
      { id: 'batery', label: 'Batery' },
      { id: 'bet_america', label: 'Bet America' },
      { id: 'betanysports', label: 'BetAnySports' },
      { id: 'betanything', label: 'BetAnything' },
      { id: 'betdsi', label: 'BetDSI' },
      { id: 'betdsi_retro_book_', label: 'BetDSI Retro' },
      { id: 'betdsi_superbook_', label: 'BetDSI SuperBook' },
      { id: 'betjack', label: 'BetJack' },
      { id: 'betmania', label: 'BetMania' },
      { id: 'betnacional', label: 'BetNacional' },
      { id: 'betnation', label: 'BetNation' },
      { id: 'betnow', label: 'BetNow' },
      { id: 'betonusa', label: 'BetOnUSA' },
      { id: 'bets_io', label: 'Bets.io' },
      { id: 'betwhale', label: 'BetWhale' },
      { id: 'bodog', label: 'Bodog' },
      { id: 'busr', label: 'BUSR' },
      { id: 'drf', label: 'DRF' },
      { id: 'everygame', label: 'Everygame' },
      { id: 'heritage', label: 'Heritage Sports' },
      { id: 'ibet', label: 'iBet' },
      { id: 'intertops', label: 'Intertops' },
      { id: 'jackpot_bet', label: 'Jackpot Bet' },
      { id: 'jazz_sports', label: 'Jazz Sports' },
      { id: 'juicebet', label: 'JuiceBet' },
      { id: 'justbet', label: 'JustBet' },
      { id: 'kutt', label: 'Kutt' },
      { id: 'looselines', label: 'LooseLines' },
      { id: 'lowvig', label: 'LowVig' },
      { id: 'lucky_casino', label: 'Lucky Casino' },
      { id: 'nitrogen', label: 'Nitrogen' },
      { id: 'oddsjam_algo_odds', label: 'OddsJam Algo' },
      { id: 'saharabets', label: 'SaharaBets' },
      { id: 'sportzino', label: 'Sportzino' },
      { id: 'sportsbetting_ag', label: 'SportsBetting.ag' },
      { id: 'sportsbetting_com', label: 'SportsBetting.com' },
      { id: 'suprabets', label: 'SupraBets' },
      { id: 'surgebet', label: 'SurgeBet' },
      { id: 'team_mexico', label: 'Team Mexico' },
      { id: 'winpot', label: 'Winpot' },
      { id: 'xbet', label: 'Xbet' },
      { id: 'youwager', label: 'YouWager' },
      { id: 'betplay', label: 'BetPlay' },
      { id: 'betmgm_uk_', label: 'BetMGM UK' },
    ],
  },
];

// ─── Query Syntax Reference ───────────────────────────────────────────────────

export const SYNTAX_PATTERNS = [
  { pattern: '[team] [market]', example: 'lakers spread', description: 'Team + market type' },
  { pattern: '[team1] vs [team2]', example: 'celtics vs bucks', description: 'Matchup — all markets for that game' },
  { pattern: '[team1] @ [team2]', example: 'warriors @ nuggets', description: 'Away @ home matchup' },
  { pattern: '[player] [N]+ [market]', example: 'lebron 25+ points', description: 'Player over threshold' },
  { pattern: '[player] under [N] [market]', example: 'steph under 4.5 threes', description: 'Player under threshold' },
  { pattern: '[player] over [N] [market]', example: 'mahomes over 300 passing yards', description: 'Player over (explicit)' },
  { pattern: 'best [league] bets', example: 'best nba bets today', description: 'Top EV bets for a league' },
  { pattern: 'best bets on [book]', example: 'best bets on fanduel', description: 'Top bets on a specific book' },
  { pattern: '[book] [league] bets', example: 'draftkings nba bets', description: 'Book + league best bets' },
  { pattern: 'live [query]', example: 'live chiefs spread', description: 'In-game/live lines' },
  { pattern: '[query] sgp', example: 'lakers sgp lebron 25+ and -3.5', description: 'Same-game parlay' },
  { pattern: '[team] [position] [market]', example: 'rams qb passing yards', description: 'Position-based player props' },
];

// ─── Keyword → Market Shortcuts ──────────────────────────────────────────────

export const MARKET_SHORTCUTS = [
  { keyword: 'ml / moneyline', resolves: 'Moneyline' },
  { keyword: 'spread / rl / puck line / run line', resolves: 'Spread / Run Line / Puck Line' },
  { keyword: 'ou / over under / total', resolves: 'Game Total' },
  { keyword: 'tt / team total', resolves: 'Team Total' },
  { keyword: 'points / pts', resolves: 'Player Points' },
  { keyword: 'assists / ast', resolves: 'Player Assists' },
  { keyword: 'rebounds / reb', resolves: 'Player Rebounds' },
  { keyword: 'threes / 3pm', resolves: 'Player Threes Made' },
  { keyword: 'passing yards / pass yds', resolves: 'Player Passing Yards' },
  { keyword: 'rushing yards / rush yds', resolves: 'Player Rushing Yards' },
  { keyword: 'receiving yards / rec yds', resolves: 'Player Receiving Yards' },
  { keyword: 'td / touchdown', resolves: 'Player Touchdowns' },
  { keyword: 'k / strikeouts', resolves: 'Pitcher Strikeouts' },
  { keyword: 'hits / h', resolves: 'Player Hits' },
  { keyword: 'goals / g', resolves: 'Player Goals' },
  { keyword: 'shots on goal / sog', resolves: 'Player Shots on Goal' },
  { keyword: 'shots / attempts', resolves: 'Player Field Goal Attempts' },
  { keyword: 'first scorer', resolves: 'First Team to Score' },
  { keyword: 'anytime td / att', resolves: 'Anytime Touchdown Scorer' },
];

// ─── Query Builder Step Definitions ──────────────────────────────────────────

export const BUILDER_STEPS = [
  { id: 'mode', label: 'Query Mode', options: ['Straight', 'SGP', 'Live'] },
  { id: 'sport', label: 'Sport' },        // dynamic from SPORTS_LEAGUES
  { id: 'league', label: 'League' },       // dynamic filtered by sport
  { id: 'subject', label: 'Team or Player', toggle: true }, // team OR player
  { id: 'market', label: 'Market' },       // dynamic from getMarketsData
  { id: 'direction', label: 'Direction', options: ['Over', 'Under', 'N/A'] },
  { id: 'line', label: 'Line Value' },     // numeric input
];

// ─── Examples Gallery ─────────────────────────────────────────────────────────
// Curated use-case query groups. Each query is directly runnable.

export const EXAMPLES_GALLERY = [
  {
    id: 'best_bets',
    label: "Tonight's Best Bets",
    icon: 'star-outline',
    color: '#fbbf24',
    description: 'Highest-EV bets across all books right now.',
    queries: [
      { label: 'Best NBA bets', query: 'best nba bets tonight' },
      { label: 'Best NFL picks', query: 'best nfl picks tonight' },
      { label: 'Best soccer bets', query: 'best premier league bets' },
      { label: 'Best across all sports', query: 'best bets tonight' },
      { label: 'Best MLB props', query: 'best mlb player props today' },
    ],
  },
  {
    id: 'player_props',
    label: 'Player Props',
    icon: 'person-outline',
    color: '#e879f9',
    description: 'Target specific stat lines for individual players.',
    queries: [
      { label: 'LeBron 25+ points', query: 'lebron 25+ points' },
      { label: 'Mahomes over 300 yards', query: 'mahomes over 300 passing yards' },
      { label: 'Jokic assists + rebounds', query: 'jokic 10+ assists' },
      { label: 'Shohei strikeouts', query: 'ohtani over 8 strikeouts' },
      { label: 'All props for a player', query: 'tyreek hill props today' },
    ],
  },
  {
    id: 'sgp',
    label: 'Same Game Parlays',
    icon: 'git-merge-outline',
    color: '#a78bfa',
    description: 'Combine multiple legs from the same game.',
    queries: [
      { label: 'Player + spread', query: 'lakers sgp lebron 25+ points and lakers -3.5' },
      { label: 'Two player props', query: 'mahomes 300+ yards and kelce 75+ receiving yards sgp' },
      { label: 'Game total + player', query: 'celtics sgp tatum 30+ and over 220' },
      { label: 'Moneyline + prop', query: 'chiefs win and mahomes td sgp' },
    ],
  },
  {
    id: 'matchups',
    label: 'Matchups',
    icon: 'swap-horizontal-outline',
    color: '#fb923c',
    description: 'All lines for a specific game.',
    queries: [
      { label: 'NBA matchup', query: 'celtics vs bucks' },
      { label: 'NFL matchup', query: 'eagles vs cowboys' },
      { label: 'Soccer match', query: 'arsenal vs liverpool' },
      { label: 'Matchup with market', query: 'warriors vs lakers spread' },
    ],
  },
  {
    id: 'live',
    label: 'Live / In-Game',
    icon: 'radio-outline',
    color: '#f87171',
    description: 'In-game lines updated in real time.',
    queries: [
      { label: 'Live NBA game', query: 'live lakers game' },
      { label: 'Live spread', query: 'live chiefs spread' },
      { label: 'Live player prop', query: 'live lebron points' },
      { label: 'Live best bets', query: 'live best bets nba' },
    ],
  },
  {
    id: 'book_specific',
    label: 'Book-Specific',
    icon: 'bookmark-outline',
    color: '#34d399',
    description: 'Best value on a specific sportsbook.',
    queries: [
      { label: 'DraftKings NBA', query: 'draftkings nba bets' },
      { label: 'FanDuel best picks', query: 'fanduel best bets tonight' },
      { label: 'Caesars NFL', query: 'caesars nfl best bets' },
      { label: 'PrizePicks NBA', query: 'prizepicks nba props' },
    ],
  },
  {
    id: 'totals',
    label: 'Totals & Spreads',
    icon: 'trending-up-outline',
    color: '#60a5fa',
    description: 'Game totals and point spreads.',
    queries: [
      { label: 'Game over/under', query: 'celtics vs heat over 215' },
      { label: 'Team total', query: 'yankees team total over 4.5 runs' },
      { label: 'Spread favorite', query: 'chiefs -6.5' },
      { label: 'Puck line', query: 'bruins puck line' },
      { label: 'Run line', query: 'dodgers run line' },
    ],
  },
];

// ─── Glossary ─────────────────────────────────────────────────────────────────

export const GLOSSARY_TERMS = [
  // Bet types
  {
    term: 'Moneyline',
    category: 'Bet Types',
    definition: 'A straight-up bet on which team or player wins. No spread involved. Odds reflect the implied probability of each outcome.',
    example: '"lakers moneyline" → returns win odds for Lakers from all books',
  },
  {
    term: 'Spread',
    category: 'Bet Types',
    definition: 'A handicap applied to even the odds between two teams. The favorite gives points, the underdog receives them. e.g. Chiefs -6.5 means the Chiefs must win by 7+ to cover.',
    example: '"chiefs -6.5" → chiefs must win by 7 or more',
  },
  {
    term: 'Game Total (O/U)',
    category: 'Bet Types',
    definition: 'A bet on the combined score of both teams. You bet over or under a line set by the sportsbook.',
    example: '"celtics vs bucks over 220" → combined points must exceed 220',
  },
  {
    term: 'Team Total',
    category: 'Bet Types',
    definition: 'Like a game total but for just one team\'s score. Useful when you have a strong view on one offense or defense.',
    example: '"yankees team total over 4.5"',
  },
  {
    term: 'Player Prop',
    category: 'Bet Types',
    definition: 'A bet on a specific statistical outcome for an individual player — points, rebounds, yards, touchdowns, strikeouts, etc.',
    example: '"lebron 25+ points" or "mahomes over 300 passing yards"',
  },
  {
    term: 'Same Game Parlay (SGP)',
    category: 'Bet Types',
    definition: 'Multiple bets combined from the same game into a single wager. Higher risk, higher payout. All legs must win. Correlation between legs affects the true odds.',
    example: '"lakers sgp lebron 25+ and lakers -3.5"',
  },
  {
    term: 'Puck Line',
    category: 'Bet Types',
    definition: 'The NHL equivalent of a spread. The standard puck line is ±1.5 goals. The favorite must win by 2+ goals; the underdog can lose by 1 or win outright.',
    example: '"bruins puck line"',
  },
  {
    term: 'Run Line',
    category: 'Bet Types',
    definition: 'The MLB equivalent of a spread. Standard run line is ±1.5 runs. The favorite must win by 2+; the underdog can lose by 1 or win outright.',
    example: '"dodgers run line"',
  },
  {
    term: 'Futures',
    category: 'Bet Types',
    definition: 'Long-term bets on outcomes that will be decided in the future, like a team winning the championship or a player winning MVP.',
    example: '"chiefs to win super bowl" or "lakers championship odds"',
  },
  // Math / EV
  {
    term: 'Expected Value (EV)',
    category: 'Math & Odds',
    definition: 'The average profit or loss per unit wagered over a large sample. Positive EV (+EV) means the bet is priced better than its true probability. EV = (Win Probability × Profit) − (Loss Probability × Stake).',
    example: 'A +5.2% EV bet returns $5.20 on average per $100 wagered long-term',
  },
  {
    term: 'Implied Probability',
    category: 'Math & Odds',
    definition: 'The probability of an outcome implied by the odds, before removing vig. For -110 odds: 110/(110+100) = 52.4% implied probability.',
    example: '-110 → 52.4% implied | +150 → 40% implied',
  },
  {
    term: 'Vig (Juice / Overround)',
    category: 'Math & Odds',
    definition: 'The sportsbook\'s built-in commission. On a typical -110/-110 line, the two implied probabilities sum to 104.8%, not 100%. The extra 4.8% is the vig.',
    example: 'Standard -110/-110 game = ~4.8% vig',
  },
  {
    term: 'Devigging',
    category: 'Math & Odds',
    definition: 'The process of removing the vig from odds to estimate the "true" no-vig probability of each outcome. Methods include additive, multiplicative, and power devigging.',
    example: 'Chiefs -110, Browns +105 → devig → Chiefs 51.8% true probability',
  },
  {
    term: 'No-Vig Fair Odds',
    category: 'Math & Odds',
    definition: 'The odds a bet would be offered at if the sportsbook took zero commission. Used as the baseline to calculate EV against market lines.',
    example: 'True probability 51.8% → fair odds = +/-103',
  },
  {
    term: 'Kelly Criterion',
    category: 'Math & Odds',
    definition: 'A bet-sizing formula that maximizes long-term bankroll growth. Kelly % = (b × p − q) / b where b = decimal odds − 1, p = win probability, q = 1 − p. Full Kelly is aggressive; many bettors use Quarter Kelly (25% of the output).',
    example: 'EV +5%, odds −110 → full Kelly = ~3.2% of bankroll',
  },
  {
    term: 'CLV (Closing Line Value)',
    category: 'Math & Odds',
    definition: 'The difference between the odds you bet at and the final closing line before the game starts. Beating the closing line consistently is strong evidence of long-term edge.',
    example: 'Bet Chiefs -3.5, line closes at -6.5 → +3 points of CLV',
  },
  {
    term: 'Sharp Money',
    category: 'Math & Odds',
    definition: 'Bets placed by professional or well-informed bettors. Sharp action tends to move lines. Opposite of "square" (recreational) money.',
    example: 'Line moves from -3.5 to -5.5 → likely sharp action on the favorite',
  },
  {
    term: 'Line Movement',
    category: 'Math & Odds',
    definition: 'How the odds on a bet change from open to close. Steam moves are fast, sharp-driven movements. Reverse line movement is when the line moves opposite to where the public is betting.',
    example: 'Chiefs open -3.5, move to -5.5 despite public betting Eagles',
  },
  // Odds formats
  {
    term: 'American Odds',
    category: 'Odds Formats',
    definition: 'The standard US odds format. Negative odds (e.g. -150) show how much you must bet to win $100. Positive odds (e.g. +130) show how much you win on a $100 bet.',
    example: '-150 → bet $150 to win $100 | +130 → bet $100 to win $130',
  },
  {
    term: 'Decimal Odds',
    category: 'Odds Formats',
    definition: 'Common in Europe and Australia. The total payout per $1 wagered including your stake. Multiply stake by decimal odds to get total return.',
    example: '2.50 decimal = +150 American | 1.91 decimal = -110 American',
  },
  {
    term: 'Fractional Odds',
    category: 'Odds Formats',
    definition: 'Common in the UK. Written as a ratio (e.g. 3/1, 5/2). The first number is profit, the second is stake.',
    example: '3/1 = win $3 for every $1 bet = +300 American',
  },
  // Platform-specific
  {
    term: 'Consensus Line',
    category: 'Platform',
    definition: 'The no-vig fair line calculated by aggregating odds across multiple sharp books. Used as the baseline for EV calculations on this platform.',
    example: 'If 5 sharp books all show -3.5 with varying vig, consensus is -3.5',
  },
  {
    term: 'Best Available Odds',
    category: 'Platform',
    definition: 'The highest odds available for a given bet across all sportsbooks in the database. Shown as the primary line on each bet card.',
    example: 'Chiefs -3.5 available at -105 on FanDuel vs -110 everywhere else → best available is -105',
  },
  {
    term: 'Anchor Book',
    category: 'Platform',
    definition: 'The sportsbook offering the best EV-adjusted price for a given market. Used to rank results and shown first on bet cards.',
    example: 'Pinnacle or sharp books often serve as anchors due to lower vig',
  },
  // Betting Slang
  {
    term: 'Chalk',
    category: 'Betting Slang',
    definition: 'The heavy favorite in a game or market. Betting the chalk means betting the obvious favorite. "Chalk bettors" almost always back favorites.',
    example: '"Lakers are chalk tonight" = Lakers are a big favorite',
  },
  {
    term: 'Dog (Underdog)',
    category: 'Betting Slang',
    definition: 'The team or player expected to lose. Underdogs have positive (+) moneyline odds. Long shots are referred to as "big dogs."',
    example: '+300 moneyline = a big dog expected to lose',
  },
  {
    term: 'Hook',
    category: 'Betting Slang',
    definition: 'A half-point added to a spread or total to eliminate the possibility of a push. "Getting the hook" means the margin lands on the spread number.',
    example: 'Chiefs -3.5 vs Chiefs -3 — the .5 is the hook',
  },
  {
    term: 'Key Number',
    category: 'Betting Slang',
    definition: 'Specific point totals where a large percentage of NFL games end (3, 7, 10, 14). Getting "through" or "off" a key number matters a lot in NFL spread betting.',
    example: 'Getting -2.5 instead of -3 in NFL is significant — 3 is the most common margin of victory',
  },
  {
    term: 'Push',
    category: 'Betting Slang',
    definition: 'When the final score falls exactly on the spread or total. Bets are refunded. e.g. If you bet Chiefs -7 and they win by exactly 7, it\'s a push.',
    example: 'Chiefs -7 win by 7 → push → stake returned',
  },
  {
    term: 'Action',
    category: 'Betting Slang',
    definition: 'A bet that stands regardless of lineup changes (e.g. the starter doesn\'t start). The opposite is "no action" which refunds the bet if conditions change.',
    example: '"Does this pitcher prop pay out if he doesn\'t start? → No, it\'s no action"',
  },
  {
    term: 'Bad Beat',
    category: 'Betting Slang',
    definition: 'Losing a bet you were heavily favored to win, usually due to a late fluky event. e.g. up by 10 at 2 minutes, last-second TD covers.',
    example: 'Chiefs +3 covering until a pick-six on the final play',
  },
  {
    term: 'Juice / Vig (Vigorish)',
    category: 'Betting Slang',
    definition: 'The sportsbook commission on every bet. Standard US juice is -110 on each side of a spread (meaning you must bet $110 to win $100).',
    example: '-110 juice = 4.8% vig | -105 "reduced juice" = 2.4% vig',
  },
  {
    term: 'Steam Move',
    category: 'Betting Slang',
    definition: 'A sudden, coordinated movement by sharp bettors that causes a rapid line shift across multiple books. A sign of informed money.',
    example: 'KC opens -3, steam move pushes it to -5 within minutes',
  },
  {
    term: 'Middling',
    category: 'Betting Slang',
    definition: 'Betting both sides of a game at different numbers to create a "middle" where both bets can win. Requires the line to move in your favor.',
    example: 'Bet Chiefs -3 early, then bet Eagles +7 later. If the Chiefs win by 4-6, both tickets cash.',
  },
  {
    term: 'ATS (Against the Spread)',
    category: 'Betting Slang',
    definition: 'A team\'s win-loss record when accounting for the point spread. A team can go 10-3 straight up but be 7-6 ATS if they often win by less than the spread.',
    example: 'Chiefs ATS record: 9-7 means they covered 9 times in 16 games',
  },
  {
    term: 'ROI (Return on Investment)',
    category: 'Betting Slang',
    definition: 'Your total profit or loss as a percentage of total money wagered. A 5% ROI on $10,000 bet = $500 net profit.',
    example: '$5,000 wagered, $250 profit = 5% ROI',
  },
  {
    term: 'Teaser',
    category: 'Betting Slang',
    definition: 'A parlay where you move the spread in your favor by a set number of points (usually 6 in NFL). The payout is lower to compensate.',
    example: 'Chiefs -7 becomes Chiefs -1 in a 6-point teaser — but you need 2+ teams',
  },
  {
    term: 'Parlay',
    category: 'Betting Slang',
    definition: 'A single bet combining two or more picks. All legs must win. Higher payout than single bets but much lower probability. Each leg multiplies the odds.',
    example: '3-team parlay at -110 each pays roughly +596 (+6×)',
  },
  {
    term: 'Reverse Line Movement (RLM)',
    category: 'Betting Slang',
    definition: 'When the line moves opposite to the direction of public betting. If 70% bet the favorite but the line moves toward the underdog, sharp money is on the dog.',
    example: 'Public 65% on Chiefs, but line drops from -7 to -6.5 → sharp money on Eagles',
  },
  {
    term: 'Handle',
    category: 'Betting Slang',
    definition: 'The total amount of money wagered on an event. A "large handle" means a lot of money was bet. Sportsbooks report handle for major events.',
    example: 'Super Bowl handle: $9 billion wagered across US books',
  },
  {
    term: 'Limit',
    category: 'Betting Slang',
    definition: 'The maximum amount a sportsbook will accept on a single bet. Sharp bettors are often limited to smaller amounts than recreational bettors.',
    example: 'Pinnacle limit: $5,000 per side on major league games',
  },
  // Common abbreviations
  {
    term: 'ML / SU / ATS / O / U',
    category: 'Abbreviations',
    definition: 'Common shorthand: ML = Moneyline (straight win), SU = Straight Up (won/loss record), ATS = Against the Spread, O = Over, U = Under.',
    example: '"Chiefs 10-3 SU, 7-6 ATS, 8-5 O/U"',
  },
  {
    term: 'PRA (Points + Rebounds + Assists)',
    category: 'Abbreviations',
    definition: 'A common NBA player prop combining points, rebounds, and assists. A single over/under line on the sum of all three stats.',
    example: '"LeBron PRA over 42.5" = points + rebounds + assists must exceed 42.5',
  },
  {
    term: 'ATTS / TD (Anytime Touchdown Scorer)',
    category: 'Abbreviations',
    definition: 'A bet that a specific player scores a touchdown at any point in the game. Very popular NFL prop market.',
    example: '"Kelce ATTS +150" = bet $100 to win $150 if Kelce scores any TD',
  },
  {
    term: 'BTTS (Both Teams to Score)',
    category: 'Abbreviations',
    definition: 'A soccer market — bet on whether both teams will score at least one goal. BTTS Yes or BTTS No.',
    example: '"Arsenal vs Liverpool BTTS Yes -120"',
  },
  {
    term: 'DNB (Draw No Bet)',
    category: 'Abbreviations',
    definition: 'A soccer market where a draw refunds your stake. Effectively betting the win, but insured against a draw.',
    example: '"Arsenal DNB -110" → if Arsenal wins, you win. Draw = money back.',
  },
  {
    term: 'K / IP / ERA',
    category: 'Abbreviations',
    definition: 'MLB pitcher stats: K = Strikeouts, IP = Innings Pitched, ERA = Earned Run Average. All are commonly used in player prop markets.',
    example: '"Ohtani over 7.5 K" = Shohei must strike out 8+ batters',
  },
  {
    term: '+EV / -EV',
    category: 'Abbreviations',
    definition: 'Positive EV (+EV) means the bet is expected to profit long-term. Negative EV (-EV) means the opposite. All recreational bets at standard book prices are technically -EV.',
    example: '+3.5% EV = expect to make $3.50 per $100 wagered on average',
  },
];

// ─── Market Descriptions ──────────────────────────────────────────────────────
// Descriptions for common markets. Keyed by exact market name from the API.

export const MARKET_DESCRIPTIONS = {
  'Moneyline': { desc: 'Bet on which team or player wins outright. No point spread.', shortcut: 'ml / moneyline' },
  'Spread': { desc: 'Handicap betting. Favorite gives points, underdog receives them.', shortcut: 'spread / rl / puck line / run line' },
  'Total': { desc: 'Combined score of both teams — bet over or under the line.', shortcut: 'ou / over under / total' },
  'Team Total': { desc: "One team's score only — over or under a set line.", shortcut: 'tt / team total' },
  'Moneyline 3-Way': { desc: 'Three-outcome moneyline (Win / Draw / Win). Common in soccer.', shortcut: 'moneyline draw' },
  'Asian Handicap': { desc: 'Eliminates the draw by using half-point or quarter-point spreads.', shortcut: 'asian handicap / ah' },
  'Both Teams to Score': { desc: 'Both teams must score at least one goal. Soccer market.', shortcut: 'btts' },
  'Double Chance': { desc: 'Covers two of three possible outcomes — Win or Draw. Soccer market.', shortcut: 'double chance' },
  'Draw No Bet': { desc: 'Moneyline where a draw refunds your stake.', shortcut: 'dnb' },
  'Player Points': { desc: 'Over/under on a player\'s total points scored.', shortcut: 'points / pts' },
  'Player Assists': { desc: 'Over/under on a player\'s total assists.', shortcut: 'assists / ast / a' },
  'Player Rebounds': { desc: 'Over/under on a player\'s total rebounds.', shortcut: 'rebounds / reb / r' },
  'Player Threes Made': { desc: 'Over/under on three-pointers made.', shortcut: 'threes / 3pm / 3s' },
  'Player Points + Rebounds + Assists': { desc: 'PRA combo — over/under on points + rebounds + assists combined.', shortcut: 'pra / pts+reb+ast' },
  'Player Passing Yards': { desc: 'Over/under on QB passing yards.', shortcut: 'passing yards / pass yds / py' },
  'Player Rushing Yards': { desc: 'Over/under on rushing yards.', shortcut: 'rushing yards / rush yds / ry' },
  'Player Receiving Yards': { desc: 'Over/under on receiving yards.', shortcut: 'receiving yards / rec yds / recy' },
  'Player Touchdowns': { desc: 'Over/under on touchdown passes, rushes, or receptions.', shortcut: 'td / touchdowns' },
  'Anytime Touchdown Scorer': { desc: 'Player scores a touchdown at any point in the game.', shortcut: 'anytime td / att' },
  'First Touchdown Scorer': { desc: 'Player scores the first TD of the game.', shortcut: 'first td' },
  'Pitcher Strikeouts': { desc: 'Over/under on strikeouts by the starting pitcher.', shortcut: 'k / ks / strikeouts' },
  'Player Hits': { desc: 'Over/under on base hits by a batter.', shortcut: 'hits / h' },
  'Player Home Runs': { desc: 'Over/under or yes/no on a player hitting a home run.', shortcut: 'hr / home run' },
  'Player Goals': { desc: 'Over/under on goals scored. Hockey or soccer.', shortcut: 'goals / g' },
  'Player Shots on Goal': { desc: 'Over/under on shots on net.', shortcut: 'shots on goal / sog' },
  'Player Saves': { desc: 'Over/under on saves by a goaltender.', shortcut: 'saves' },
  'First Blood': { desc: 'Which team/player draws first blood in a CS2/esports match.', shortcut: 'first blood / fb' },
  'Map Winner': { desc: 'Which team wins an individual map in esports.', shortcut: 'map winner' },
};
